import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import type { FlatfinderPerspective } from 'realitiq-utils/src/flatfinder';
import { SimpleConfirmModal } from '@/components/modals/new/simple-confirmation-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { uploadImage } from '@/features/project-media/api/upload-image';
import { getFlatfinderImageUrl } from './display';
import { FlatfinderPerspectiveError, prepareFlatfinderPerspectiveImage } from './perspective-image';
import { PerspectiveImageUpload } from './perspective-image-upload';

export function PerspectiveForm({
  projectId,
  initial,
  onSave,
  onCancel,
  onBusyChange,
}: {
  projectId: string;
  initial?: FlatfinderPerspective;
  onSave: (perspective: FlatfinderPerspective) => void;
  onCancel: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [replaceImageConfirmation, setReplaceImageConfirmation] = useState(false);

  async function applyPerspective() {
    setBusy(true);
    onBusyChange?.(true);
    try {
      let image = initial
        ? {
            imageId: initial.imageId,
            mediaId: initial.mediaId,
            width: initial.width,
            height: initial.height,
          }
        : null;
      if (file) {
        if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type))
          throw new FlatfinderPerspectiveError('flatfinderFormChooseImageFormat');
        const { file: prepared, ...size } = await prepareFlatfinderPerspectiveImage(file);
        if (!size.width || !size.height)
          throw new FlatfinderPerspectiveError('flatfinderFormImageReadFailed');
        const [uploaded] = await uploadImage({ image: prepared, projectId });
        if (!uploaded?.mediaId)
          throw new FlatfinderPerspectiveError('flatfinderFormImageUploadIncomplete');
        image = { imageId: uploaded.id, mediaId: uploaded.mediaId, ...size };
      }
      if (!image) throw new FlatfinderPerspectiveError('flatfinderFormChooseImage');
      onSave({
        id: initial?.id ?? crypto.randomUUID(),
        ...image,
        name: name.trim(),
        description: description.trim(),
        sortOrder: initial?.sortOrder ?? 0,
        regions: file ? [] : (initial?.regions ?? []),
      });
    } catch (cause) {
      if (cause instanceof FlatfinderPerspectiveError) toast.error(t(cause.translationKey));
      else toast.error(t('flatfinderFormImageUploadFailed'));
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }

  return (
    <>
      <SimpleConfirmModal
        modal={{
          isOpen: replaceImageConfirmation,
          open: () => setReplaceImageConfirmation(true),
          dismiss: () => setReplaceImageConfirmation(false),
        }}
        title={t('flatfinderFormReplaceImage')}
        body={t('flatfinderFormReplaceImageConfirmation')}
        confirmButtonText={t('flatfinderFormConfirmReplaceImage')}
        confirmButtonVariant="destructive"
        onConfirm={applyPerspective}
      />
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) {
            toast.error(t('flatfinderFormEnterPerspectiveName'));
            return;
          }
          if (!initial && !file) {
            toast.error(t('flatfinderFormChooseImage'));
            return;
          }
          if (file && initial?.regions.length) {
            setReplaceImageConfirmation(true);
            return;
          }
          void applyPerspective();
        }}
      >
        <fieldset disabled={busy} className="grid items-start gap-3 sm:grid-cols-2">
          <Input
            autoFocus
            label={t('nameRequired')}
            required
            maxLength={200}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <label className="flex w-full cursor-text flex-col gap-[10px]">
            <span className="select-none text-xs text-text-primary">{t('description')}</span>
            <textarea
              maxLength={10000}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className="h-[49px] w-full resize-none rounded-xs border-2 border-[--input-color] bg-primary-lighter px-4 py-3 text-sm text-text-primary shadow-sm transition-colors [--input-color:hsla(var(--primary-color-light))] [--input-focus-color:hsla(var(--primary-color))] focus-visible:border-[--input-focus-color] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </label>
          <PerspectiveImageUpload
            label={initial ? t('flatfinderFormReplaceImage') : t('imageRequired')}
            file={file}
            required={!initial}
            disabled={busy}
            currentImageUrl={
              initial ? getFlatfinderImageUrl(initial.mediaId, null, projectId) : undefined
            }
            onChange={setFile}
          />
        </fieldset>
        <div className="flex justify-end gap-2">
          <Button type="button" disabled={busy} onClick={onCancel} variant="outline">
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={busy} loading={busy}>
            {busy ? t('uploading') : t('flatfinderFormApplyLayout')}
          </Button>
        </div>
      </form>
    </>
  );
}
